import { type JanusCtx, type Row, itemField, record, upper, definitionNotHeld } from './context.js';
import type { JanusInput } from './janus.js';
import { entryId, layerListEntryUuid } from '../actions/spell-components.js';

export type SpellGrantMode = 'at_will_spell' | 'innate_spell_per_long_rest';
export interface SpellGrantCastInput { mode: SpellGrantMode; sourceId?: string }
export function isSpellGrantMode(value: unknown): value is SpellGrantMode {
  return value === 'at_will_spell' || value === 'innate_spell_per_long_rest';
}

/** spell_is_cast_from_magical_item(): a sheet source must resolve to an inventory UUID; native grants keep Bending. */
export function spellGrantFromInventory(ctx: JanusCtx, input: JanusInput): boolean {
  if (!isSpellGrantMode(input.useAbilityVariable1)) return false;
  const source = ctx.read.resolveInventory?.(upper(input.useAbilityVariable2)) ?? null;
  return /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(upper(ctx.read.inventoryField?.(source, 'inventory_id')));
}

/** A missing lane still tells a playtester which variable contract was requested. */
export function abilityVariableModeReason(variable1: unknown, variable2?: unknown): string {
  const mode = String(variable1 ?? '').trim() || (variable2 == null || variable2 === '' ? 'empty' : 'secondary-choice');
  return `janus:variable-mode:${mode.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 96)}`;
}

/** A source UUID from the sheet is provenance, while a damage/summon/level choice stays a separate contract. */
export function isSpellGrantCast(ctx: JanusCtx, ability: Row, caster: Row, input: JanusInput): boolean {
  if (!['spell', 'curse'].includes(String(itemField(ability, 'item_type'))) || !isSpellGrantMode(input.useAbilityVariable1)) return false;
  if (input.useAbilityVariable2 == null || input.useAbilityVariable2 === '') return true;
  const source = upper(input.useAbilityVariable2), id = upper(itemField(ability, 'item_id'));
  for (const entry of ctx.read.entries(caster, input.useAbilityVariable1)) {
    const row = record(entry);
    if (!row || upper(ctx.read.value(row, 'source')) !== source) continue;
    const grantId = entryId(row);
    if (grantId === id) return true;
    const parent = ctx.read.definition(grantId);
    if (!parent) definitionNotHeld('spell_grant:definition-not-held', grantId);
    if (ctx.read.entries(parent, 'ability_choice').some(child => upper(layerListEntryUuid(child)) === id)) return true;
  }
  return false;
}
/** template_spell_roll.php's free modes, including the mode saved on a timed finish. */
export function spellCastIsFree(ctx: JanusCtx, input: JanusInput): boolean {
  if (ctx.trigger && input.useAbilityVariable2 === 'arcane_arrow_cast' && ctx.arcaneArrowDelivery?.caster_id === upper(input.casterId)
      && ctx.arcaneArrowDelivery.spell_id === upper(input.abilityId)) return true;
  if (isSpellGrantMode(input.useAbilityVariable1) || ctx.itemCast) return true;
  const finish = ctx.timedFinish?.casterId === upper(input.casterId) && ctx.timedFinish?.abilityId === upper(input.abilityId);
  return !!finish && (input.useAbilityVariable1 === 'finish_casting_ritual_spell'
    || (input.useAbilityVariable1 === 'finish_casting'
      && ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability'].includes(String(input.useAbilityVariable2))));
}

